<template>
  <div class="share-page">
    <header class="share-header">
      <div class="share-header__inner">
        <router-link to="/" class="share-header__logo">
          <span class="share-header__logo-go">Go</span> Trust
        </router-link>
        <button type="button" class="share-header__dl" @click="goDownload">
          下载 App
        </button>
      </div>
    </header>

    <main class="share-main">
      <div v-if="loading" class="share-state">加载中…</div>
      <div v-else-if="error" class="share-state share-state--error">
        <p>{{ error }}</p>
        <button type="button" class="share-retry" @click="load">重试</button>
      </div>
      <template v-else-if="teacher">
        <section class="profile-card">
          <div class="profile-card__avatar">
            <img
              v-if="teacher.avatar"
              :src="teacher.avatar"
              :alt="displayName"
            />
            <span v-else>{{ displayName.slice(0, 1) }}</span>
          </div>
          <h1 class="profile-card__name">{{ displayName }}</h1>
          <div v-if="skills.length" class="tag-row">
            <span v-for="(skill, i) in skills" :key="i" class="tag">
              {{ skill }}
            </span>
          </div>
        </section>

        <section v-if="teacher.summary" class="block-card">
          <h2 class="block-card__title">个人简介</h2>
          <p class="block-card__text">{{ teacher.summary }}</p>
        </section>

        <section v-if="educations.length" class="block-card">
          <h2 class="block-card__title">教育经历</h2>
          <ul class="edu-list">
            <li v-for="(edu, index) in educations" :key="edu.id || index">
              <p class="edu-list__uni">
                {{ edu.university || '院校' }}
                <span v-if="edu.degree" class="edu-list__deg">
                  · {{ edu.degree }}
                </span>
              </p>
              <p v-if="edu.major" class="edu-list__sub">{{ edu.major }}</p>
              <p v-if="edu.date_range" class="edu-list__sub">
                {{ edu.date_range }}
              </p>
            </li>
          </ul>
        </section>

        <section v-if="awardImages.length" class="block-card">
          <h2 class="block-card__title">获奖经历</h2>
          <div class="award-scroller">
            <div
              v-for="(img, index) in awardImages"
              :key="index"
              class="award-scroller__item"
              @click="openPreview(index)"
            >
              <img :src="img" :alt="'获奖证书 ' + (index + 1)" />
            </div>
          </div>
        </section>

        <section v-if="researches.length" class="block-card">
          <h2 class="block-card__title">研究经历</h2>
          <ul class="edu-list">
            <li v-for="(research, index) in researches" :key="research.id || index">
              <p class="edu-list__uni">
                {{ researchTitle(research) }}
              </p>
              <p v-if="research.industry" class="edu-list__sub">
                {{ research.industry }}
              </p>
              <p v-if="researchDateRange(research)" class="edu-list__sub">
                {{ researchDateRange(research) }}
              </p>
            </li>
          </ul>
        </section>

        <section v-if="papers.length" class="block-card">
          <h2 class="block-card__title">论文刊物</h2>
          <ul class="edu-list">
            <li v-for="(paper, index) in papers" :key="paper.id || index">
              <p class="edu-list__uni">{{ paper.name || '论文' }}</p>
              <p v-if="paperSubtitle(paper)" class="edu-list__sub">
                {{ paperSubtitle(paper) }}
              </p>
              <p v-if="paper.publication_date" class="edu-list__sub">
                {{ paper.publication_date }}
              </p>
            </li>
          </ul>
        </section>

        <section v-if="courses.length" class="block-card">
          <h2 class="block-card__title">任教课程</h2>
          <ul class="course-list">
            <li
              v-for="(c, index) in courses"
              :key="c.id || index"
              @click="goCourse(c.id)"
            >
              <div class="course-list__main">
                <p class="course-list__name">{{ c.name || '课程' }}</p>
                <p v-if="c.price != null && c.price !== ''" class="course-list__price">
                  ￥{{ String(c.price).replace(/^￥|^¥/, '') }}
                </p>
              </div>
              <span class="course-list__arrow">›</span>
            </li>
          </ul>
        </section>
      </template>
    </main>

    <ShareBottomBar @open-app="goDownload" />

    <div v-if="previewUrl" class="preview" @click="closePreview">
      <img :src="previewUrl" alt="获奖证书预览" class="preview__img" />
    </div>
  </div>
</template>

<script>
import ShareBottomBar from '../components/ShareBottomBar.vue'
import { fetchTeacherInfo } from '../api/share'
import { setShareMeta } from '../utils/shareMeta'

export default {
  name: 'TeacherSharePage',
  components: { ShareBottomBar },
  data() {
    return {
      loading: true,
      error: '',
      teacher: null,
      previewIndex: -1,
    }
  },
  computed: {
    teacherId() {
      return this.$route.params.id
    },
    displayName() {
      const t = this.teacher
      if (!t) return '老师'
      return t.nickname || t.username || t.name || '老师'
    },
    skills() {
      const raw = this.teacher?.skill
      if (!raw) return []
      return String(raw)
        .split(/[,，/、|]/)
        .map((s) => s.trim())
        .filter(Boolean)
    },
    educations() {
      const list = this.teacher?.education
      return Array.isArray(list) ? list : []
    },
    courses() {
      const list = this.teacher?.course
      return Array.isArray(list) ? list : []
    },
    awardImages() {
      const t = this.teacher
      if (!t) return []
      const images = []
      const pushImage = (item) => {
        if (!item || typeof item !== 'object') return
        const url = (item.image || '').toString().trim()
        if (url) images.push(url)
      }
      const structured = t.awards
      if (Array.isArray(structured)) structured.forEach(pushImage)
      const raw = t.award
      if (Array.isArray(raw)) raw.forEach(pushImage)
      return images
    },
    researches() {
      const list = this.teacher?.research ?? this.teacher?.researches
      return Array.isArray(list) ? list : []
    },
    papers() {
      const list = this.teacher?.paper ?? this.teacher?.papers
      return Array.isArray(list) ? list : []
    },
    shareTitle() {
      const name = this.displayName.replace(/老师$/, '').trim()
      return name ? `名师${name}` : '老师详情'
    },
    previewUrl() {
      if (this.previewIndex < 0) return ''
      return this.awardImages[this.previewIndex] || ''
    },
  },
  watch: {
    teacherId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    async load() {
      const id = this.teacherId
      if (!id) {
        this.loading = false
        this.error = '缺少老师 ID'
        this.teacher = null
        return
      }
      this.loading = true
      this.error = ''
      try {
        this.teacher = await fetchTeacherInfo(id)
        const desc = (this.teacher?.summary || '').trim()
        setShareMeta({
          title: `${this.shareTitle} - Go Trust`,
          description: desc
            ? desc.slice(0, 80)
            : '名师课程、留学资讯一站式服务，打开查看详情并下载 App',
          url: window.location.href,
        })
      } catch (e) {
        this.teacher = null
        this.error = e?.message || '加载失败'
        setShareMeta({ title: '老师详情 - Go Trust' })
      } finally {
        this.loading = false
      }
    },
    researchTitle(research) {
      const parts = [research.organization, research.project]
        .map((s) => (s ? String(s).trim() : ''))
        .filter(Boolean)
      return parts.join(' · ') || '研究经历'
    },
    researchDateRange(research) {
      const start = (research.start_date || research.startDate || '').trim()
      const end = (research.end_date || research.endDate || '').trim()
      if (!start && !end) return ''
      if (!start) return end
      if (!end) return start
      return `${start} - ${end}`
    },
    paperSubtitle(paper) {
      return [paper.journal, paper.number]
        .map((s) => (s ? String(s).trim() : ''))
        .filter(Boolean)
        .join(' · ')
    },
    goDownload() {
      this.$router.push('/download')
    },
    openPreview(index) {
      this.previewIndex = index
    },
    closePreview() {
      this.previewIndex = -1
    },
    goCourse(id) {
      if (!id) return
      this.$router.push(`/course/${id}`)
    },
  },
}
</script>

<style scoped>
.share-page {
  min-height: 100vh;
  background: var(--color-bg);
  padding-bottom: 88px;
}

.share-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--color-primary);
  height: var(--header-height);
}

.share-header__inner {
  max-width: 720px;
  margin: 0 auto;
  height: 100%;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.share-header__logo {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.share-header__logo-go {
  color: var(--color-accent);
}

.share-header__dl {
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.share-main {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}

.share-state {
  padding: 64px 16px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.share-state--error {
  color: #c0392b;
}

.share-retry {
  margin-top: 12px;
  height: 36px;
  padding: 0 16px;
  border-radius: 18px;
  background: var(--color-primary);
  color: #fff;
  font-size: 13px;
}

.profile-card,
.block-card {
  background: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  margin-bottom: 12px;
}

.profile-card {
  padding: 24px 16px 20px;
  text-align: center;
}

.profile-card__avatar {
  width: 88px;
  height: 88px;
  margin: 0 auto 12px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-tag-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text-secondary);
}

.profile-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-card__name {
  font-size: 22px;
  font-weight: 700;
}

.tag-row {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--color-tag-bg);
  color: var(--color-text-secondary);
  font-size: 12px;
}

.block-card {
  padding: 16px;
}

.block-card__title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.block-card__text {
  font-size: 14px;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  line-height: 1.7;
}

.edu-list,
.course-list {
  list-style: none;
}

.award-scroller {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 4px;
}

.award-scroller::-webkit-scrollbar {
  display: none;
}

.award-scroller__item {
  flex-shrink: 0;
  width: 96px;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-tag-bg);
  cursor: pointer;
}

.award-scroller__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.preview__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.edu-list li {
  padding: 10px 0;
  border-top: 1px solid var(--color-border);
}

.edu-list li:first-child {
  border-top: none;
  padding-top: 0;
}

.edu-list__uni {
  font-size: 14px;
  font-weight: 600;
}

.edu-list__deg {
  font-weight: 400;
  color: var(--color-text-secondary);
}

.edu-list__sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--color-text-secondary);
}

.course-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid var(--color-border);
  cursor: pointer;
}

.course-list li:first-child {
  border-top: none;
  padding-top: 0;
}

.course-list__main {
  flex: 1;
  min-width: 0;
}

.course-list__name {
  font-size: 14px;
  font-weight: 600;
}

.course-list__price {
  margin-top: 2px;
  font-size: 13px;
  color: var(--color-accent);
  font-weight: 600;
}

.course-list__arrow {
  color: var(--color-text-light);
  font-size: 20px;
}
</style>
